import { Fragment, useCallback, useMemo } from 'react';
import skin from '~/assets/icons/skin.svg';
import {
	PropertyContainer,
	PropertyContainerRow,
} from '~/components/ui/PropertyContainer';
import { SelectColor } from '~/components/ui/SelectColor';
import { SelectItem } from '~/components/ui/SelectItem';
import { Slider } from '~/components/ui/Slider';
import { Text } from '~/components/ui/Text';
import { rage, useObservable } from '~/rage';
import { IPedOverlayValue, PedOverlaySection } from '~/shared';
import {
	setOverlayValues,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { SectionHeading } from '../SectionHeading';
import { makeupIcons, makeupSections, skinSections } from './MakeupTab.model';
import {
	StyledDivider,
	StyledSection,
	StyledSettingsContainer,
} from './MakeupTab.styled';

const MakeupTab: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const overlaySectionConfigs = useObservable(
		rage.characterCreation.getOverlaySectionConfigs(),
	);
	const overlayConfigs = useLayoutSelector(
		(state) => state.characterCreation.overlayConfigs,
	);
	const overlayValues = useLayoutSelector(
		(state) => state.characterCreation.overlayValues,
	);
	const makeupColors = useObservable(rage.characterCreation.getMakeupColors());

	const filteredOverlayConfigs = useMemo(
		() =>
			overlayConfigs.filter((item) => makeupSections.includes(item.section)),
		[overlayConfigs],
	);

	const hasAnySkinItems = useMemo(
		() =>
			skinSections.some((section) =>
				overlayConfigs.some((item) => item.section === section),
			),
		[overlayConfigs],
	);

	const handleChangeStyle = useCallback(
		async (section: PedOverlaySection, index: number) => {
			const valueIndex = overlayValues.findIndex(
				(element) => element.section === section,
			);
			if (valueIndex === -1) {
				return;
			}

			const newValue = { ...overlayValues[valueIndex], index };

			const newValues = overlayValues.map((item, i) =>
				i === valueIndex ? newValue : item,
			);

			dispatch(setOverlayValues(newValues));
			await rage.characterCreation.setOverlayValues([newValue]);
		},
		[overlayValues],
	);

	const handleChangeColor = useCallback(
		(field: keyof IPedOverlayValue, section: PedOverlaySection) =>
			async (value: string) => {
				const valueIndex = overlayValues.findIndex(
					(element) => element.section === section,
				);
				if (valueIndex === -1) {
					return;
				}

				const selectedColorIndex = makeupColors.find(
					(color) => color.hex === value,
				)?.index;

				const newValue = {
					...overlayValues[valueIndex],
					[field]: selectedColorIndex,
				};

				const newValues = overlayValues.map((item, i) =>
					i === valueIndex ? newValue : item,
				);

				dispatch(setOverlayValues(newValues));
				await rage.characterCreation.setOverlayValues([newValue]);
			},
		[overlayValues, makeupColors],
	);

	const handleChangeOpacity = useCallback(
		(section: PedOverlaySection) => async (value: number) => {
			const valueIndex = overlayValues.findIndex(
				(element) => element.section === section,
			);
			if (valueIndex === -1) {
				return;
			}

			const newValue = { ...overlayValues[valueIndex], colorOpacity: value };

			const newValues = overlayValues.map((item, i) =>
				i === valueIndex ? newValue : item,
			);

			dispatch(setOverlayValues(newValues));
			await rage.characterCreation.setOverlayValues([newValue]);
		},
		[overlayValues],
	);

	return (
		<StyledSettingsContainer>
			{makeupSections.map((section, sectionIndex) => {
				const items = filteredOverlayConfigs.filter(
					(item) => item.section === section,
				);
				if (!items.length) {
					return null;
				}
				const value = overlayValues.find((value) => value.section === section);
				const overlayValue = items.find((item) => item.index === value?.index);
				const overlaySectionConfig = overlaySectionConfigs.find(
					(config) => config.section === section,
				);
				const colorPrimary = makeupColors.find(
					(color) => color.index === value?.colorPrimary,
				);
				const colorSecondary = makeupColors.find(
					(color) => color.index === value?.colorSecondary,
				);

				const selectItemValue = overlayValue?.name
					? {
							value: overlayValue.index,
							title: overlayValue.name,
					  }
					: {
							value: items[0].index,
							title: items[0].name,
					  };

				return (
					<StyledSection key={sectionIndex}>
						<SectionHeading
							icon={makeupIcons[section] || ''}
							text={overlaySectionConfig?.name || ''}
						/>
						<PropertyContainer>
							<PropertyContainerRow>
								<SelectItem
									value={selectItemValue}
									items={items.map((overlay) => ({
										value: overlay.index,
										title: overlay.name,
									}))}
									onChange={(value) => handleChangeStyle(section, value.value)}
									count
									fullWidth
									loop
								/>
							</PropertyContainerRow>
							{overlaySectionConfig?.withColor && (
								<Fragment>
									<PropertyContainerRow>
										<Text>Основной цвет</Text>
										<SelectColor
											value={colorPrimary?.hex}
											items={makeupColors.map((item) => item.hex)}
											onChange={handleChangeColor('colorPrimary', section)}
										/>
									</PropertyContainerRow>
									<PropertyContainerRow>
										<Text>Доп. цвет</Text>
										<SelectColor
											value={colorSecondary?.hex}
											items={makeupColors.map((item) => item.hex)}
											onChange={handleChangeColor('colorSecondary', section)}
										/>
									</PropertyContainerRow>
								</Fragment>
							)}
							{overlaySectionConfig?.withOpacity && (
								<PropertyContainerRow>
									<Text>Насыщенность</Text>
									<Slider
										min={0}
										max={1}
										value={value?.colorOpacity || 0}
										onChange={handleChangeOpacity(section)}
									/>
								</PropertyContainerRow>
							)}
						</PropertyContainer>
					</StyledSection>
				);
			})}
			<StyledSection>
				{hasAnySkinItems && <SectionHeading icon={skin} text={'Кожа'} />}
				<PropertyContainer>
					{skinSections.map((section, sectionIndex) => {
						const items = overlayConfigs.filter(
							(item) => item.section === section,
						);
						if (!items.length) {
							return null;
						}
						const value = overlayValues.find(
							(value) => value.section === section,
						);
						const skinValue = items.find((item) => item.index === value?.index);
						const overlaySectionConfig = overlaySectionConfigs.find(
							(config) => config.section === section,
						);
						const colorPrimary = makeupColors.find(
							(color) => color.index === value?.colorPrimary,
						);
						const colorSecondary = makeupColors.find(
							(color) => color.index === value?.colorSecondary,
						);

						const selectItemValue = skinValue?.name
							? {
									value: skinValue.index,
									title: skinValue.name,
							  }
							: {
									value: items[0].index,
									title: items[0].name,
							  };

						return (
							<Fragment key={sectionIndex}>
								<PropertyContainerRow>
									<Text>{overlaySectionConfig?.name}</Text>
									<SelectItem
										value={selectItemValue}
										items={items.map((skin) => ({
											value: skin.index,
											title: skin.name,
										}))}
										onChange={(value) =>
											handleChangeStyle(section, value.value)
										}
										onlyCount
										loop
									/>
								</PropertyContainerRow>
								{overlaySectionConfig?.withColor && (
									<Fragment>
										<PropertyContainerRow>
											<Text>Основной цвет</Text>
											<SelectColor
												value={colorPrimary?.hex}
												items={makeupColors.map((item) => item.hex)}
												onChange={handleChangeColor('colorPrimary', section)}
											/>
										</PropertyContainerRow>
										<PropertyContainerRow>
											<Text>Доп. цвет</Text>
											<SelectColor
												value={colorSecondary?.hex}
												items={makeupColors.map((item) => item.hex)}
												onChange={handleChangeColor('colorSecondary', section)}
											/>
										</PropertyContainerRow>
									</Fragment>
								)}
								{overlaySectionConfig?.withOpacity && (
									<PropertyContainerRow>
										<Text>Насыщенность</Text>
										<Slider
											min={0}
											max={1}
											value={value?.colorOpacity || 0}
											onChange={handleChangeOpacity(section)}
											valueInput
										/>
									</PropertyContainerRow>
								)}
								<StyledDivider />
							</Fragment>
						);
					})}
				</PropertyContainer>
			</StyledSection>
		</StyledSettingsContainer>
	);
};

export { MakeupTab };
